import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Mail, Shield, ArrowRight, Lock, RefreshCw } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminLogin = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState('email'); // 'email' | 'otp'
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const otpRefs = useRef([]);

  // Countdown timer for resend
  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  // Focus first OTP input when step changes to OTP
  useEffect(() => {
    if (step === 'otp' && otpRefs.current[0]) {
      otpRefs.current[0].focus();
    }
  }, [step]);

  const handleSendOTP = async (e) => {
    e.preventDefault();
    
    if (!email.trim()) {
      toast.error('Please enter your email');
      return;
    }

    try {
      setLoading(true);
      await axios.post(`${API}/auth/admin/send-otp`, { email });
      
      toast.success('OTP sent to your email');
      setStep('otp');
      setResendTimer(60); // 60 seconds before resend
    } catch (error) {
      console.error('Send OTP error:', error);
      toast.error(error.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleOTPChange = (index, value) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) return;
    
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    
    // Auto-focus next input
    if (value && index < 5) {
      otpRefs.current[index + 1]?.focus();
    }
  };

  const handleOTPKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleOTPPaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 6);
    if (/^\d+$/.test(pastedData)) {
      const newOtp = pastedData.split('').concat(Array(6 - pastedData.length).fill(''));
      setOtp(newOtp);
      // Focus the last filled input or the next empty one
      const focusIndex = Math.min(pastedData.length, 5);
      otpRefs.current[focusIndex]?.focus();
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    
    const otpString = otp.join('');
    if (otpString.length !== 6) {
      toast.error('Please enter complete 6-digit OTP');
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post(`${API}/auth/admin/verify-otp`, {
        email,
        otp: otpString
      });
      
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      
      toast.success('Login successful!');
      navigate('/admin');
    } catch (error) {
      console.error('Verify OTP error:', error);
      toast.error(error.response?.data?.detail || 'Invalid OTP');
      // Clear OTP on error
      setOtp(['', '', '', '', '', '']);
      otpRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    if (resendTimer > 0) return;
    
    try {
      setLoading(true);
      await axios.post(`${API}/auth/admin/send-otp`, { email });
      
      toast.success('New OTP sent');
      setResendTimer(60);
      setOtp(['', '', '', '', '', '']);
      otpRefs.current[0]?.focus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend OTP');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0d0d0d] flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        {/* Logo/Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-[#D4AF37] rounded-2xl mb-4">
            <Shield className="w-8 h-8 text-[#0d0d0d]" />
          </div>
          <h1 className="font-playfair text-3xl font-bold text-white mb-2">
            Admin Portal
          </h1>
          <p className="font-manrope text-sm text-gray-400">
            BusinessMint PR
          </p>
        </div>
        
        {/* Login Card */}
        <div className="bg-white border border-gray-200 rounded-xl p-8">
          
          {step === 'email' ? (
            /* Step 1: Email Input */
            <form onSubmit={handleSendOTP}>
              <div className="text-center mb-6">
                <h2 className="font-playfair text-xl font-bold text-[#1a1a1a] mb-2">
                  Sign In with OTP
                </h2>
                <p className="font-manrope text-sm text-gray-500">
                  Enter your admin email to receive a one-time password
                </p>
              </div>
              
              <div className="mb-6">
                <label className="font-manrope text-xs text-gray-600 mb-2 block uppercase tracking-widest">
                  Admin Email
                </label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="info@businessmint.in"
                    data-testid="admin-email-input"
                    required
                    className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-lg font-manrope text-sm focus:ring-2 focus:ring-[#D4AF37]/20 focus:border-[#D4AF37] focus:outline-none"
                  />
                </div>
              </div>
              
              <button
                type="submit"
                disabled={loading}
                data-testid="send-otp-button"
                className="w-full px-8 py-4 bg-[#D4AF37] text-[#0d0d0d] rounded-lg font-manrope text-sm uppercase tracking-widest font-semibold hover:bg-[#B8960F] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    Send OTP
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </form>
          ) : (
            /* Step 2: OTP Input */
            <form onSubmit={handleVerifyOTP}>
              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-[#D4AF37]/10 rounded-full mb-4">
                  <Lock className="w-6 h-6 text-[#D4AF37]" />
                </div>
                <h2 className="font-playfair text-xl font-bold text-[#1a1a1a] mb-2">
                  Enter OTP
                </h2>
                <p className="font-manrope text-sm text-gray-500">
                  We sent a 6-digit code to<br />
                  <span className="font-semibold text-[#1a1a1a]">{email}</span>
                </p>
              </div>
              
              {/* OTP Input Boxes */}
              <div className="flex justify-center gap-2 mb-6" onPaste={handleOTPPaste}>
                {otp.map((digit, index) => (
                  <input
                    key={index}
                    ref={(el) => (otpRefs.current[index] = el)}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleOTPChange(index, e.target.value)}
                    onKeyDown={(e) => handleOTPKeyDown(index, e)}
                    data-testid={`otp-input-${index}`}
                    className="w-12 h-14 text-center text-xl font-bold border-2 border-gray-200 rounded-lg focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/20 focus:outline-none transition-colors"
                  />
                ))}
              </div>
              
              <button
                type="submit"
                disabled={loading || otp.join('').length !== 6}
                data-testid="verify-otp-button"
                className="w-full px-8 py-4 bg-[#D4AF37] text-[#0d0d0d] rounded-lg font-manrope text-sm uppercase tracking-widest font-semibold hover:bg-[#B8960F] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 mb-4"
              >
                {loading ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  'Verify & Login'
                )}
              </button>
              
              {/* Resend & Back options */}
              <div className="flex items-center justify-between text-sm">
                <button
                  type="button"
                  onClick={() => {
                    setStep('email');
                    setOtp(['', '', '', '', '', '']);
                  }}
                  className="font-manrope text-gray-500 hover:text-[#1a1a1a] transition-colors"
                >
                  ← Change email
                </button>
                
                <button
                  type="button"
                  onClick={handleResendOTP}
                  disabled={resendTimer > 0 || loading}
                  className={`font-manrope ${
                    resendTimer > 0 
                      ? 'text-gray-400 cursor-not-allowed' 
                      : 'text-[#D4AF37] hover:text-[#B8960F]'
                  } transition-colors`}
                >
                  {resendTimer > 0 ? `Resend in ${resendTimer}s` : 'Resend OTP'}
                </button>
              </div>
            </form>
          )}
        </div>
        
        {/* Security notice */}
        <p className="text-center text-gray-500 text-xs mt-6 font-manrope">
          Secure OTP-based authentication
        </p>
      </div>
    </div>
  );
};

export default AdminLogin;
